import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import CourseMap from './masterplan/CourseMap';
import HolePanel from './masterplan/HolePanel';
import ScoreCard from './masterplan/ScoreCard';
import { HOLES, TOTAL_PAR, totalFor } from './masterplan/courseData';
import { useReducedMotion } from './masterplan/useReducedMotion';

const GolfMasterPlan = () => {
  const [hole, setHole] = useState(null);
  const [tee, setTee] = useState('noir');
  const [view, setView] = useState('plan');
  const [playing, setPlaying] = useState(false);
  const [scoreOpen, setScoreOpen] = useState(false);
  const timerRef = useRef(null);
  const reduced = useReducedMotion();

  const clearTimer = () => { clearTimeout(timerRef.current); timerRef.current = null; };
  useEffect(() => clearTimer, []);

  const selectHole = useCallback((n) => {
    clearTimer();
    setPlaying(false);
    setHole(n);
  }, []);

  const handleHoleEnd = useCallback((n) => {
    if (!playing) return;
    if (n >= HOLES.length) { setPlaying(false); return; }
    clearTimer();
    timerRef.current = setTimeout(() => setHole(n + 1), 900);
  }, [playing]);

  const startTour = () => {
    clearTimer();
    setPlaying(true);
    setHole(null);
    timerRef.current = setTimeout(() => setHole(1), 50);
  };

  const stopTour = () => { clearTimer(); setPlaying(false); };
  const reset = () => { clearTimer(); setPlaying(false); setHole(null); };

  return (
    <section id="masterplan" className="relative bg-[#122a1e] overflow-hidden" data-testid="masterplan-section">
      <div className="absolute inset-0 bg-gradient-to-b from-[#122a1e] via-[#1a3c2a] to-[#122a1e]" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#c9a962]/50 to-transparent" />
      <div className="absolute top-16 left-10 w-px h-32 bg-gradient-to-b from-[#c9a962]/40 to-transparent hidden lg:block" />
      <div className="absolute top-16 right-10 w-px h-32 bg-gradient-to-b from-[#c9a962]/40 to-transparent hidden lg:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 lg:pt-20 pb-16 lg:pb-20">
        <div className="max-w-3xl mx-auto text-center mb-8 lg:mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-px bg-[#c9a962]" />
            <span className="font-montserrat text-[#c9a962] text-xs tracking-[0.3em] uppercase">Master plan interactif</span>
            <div className="w-12 h-px bg-[#c9a962]" />
          </div>
          <h2 className="font-playfair text-4xl lg:text-5xl text-white leading-tight mb-3">
            Le Master <span className="text-[#c9a962] italic">Plan</span>
          </h2>
          <p className="font-montserrat text-white/50 text-sm lg:text-base leading-relaxed max-w-xl mx-auto">
            Explorez le domaine trou par trou : choisissez un départ, suivez chaque tracé et découvrez
            les lots, le Club House et les équipements du Golf de Songon M'braté.
          </p>
          <p className="font-playfair text-[#c9a962] text-lg lg:text-xl mt-5 flex items-center justify-center flex-wrap gap-x-3" data-testid="masterplan-stats">
            <span>{HOLES.length} trous</span>
            <span className="text-[#c9a962]/50" aria-hidden="true">•</span>
            <span>Par {TOTAL_PAR}</span>
            <span className="text-[#c9a962]/50" aria-hidden="true">•</span>
            <span data-testid="masterplan-total">{totalFor(tee).toLocaleString('fr-FR')} m</span>
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[7fr_3fr] gap-5 lg:gap-6 items-stretch">
          <CourseMap
            hole={hole} tee={tee} view={view} reduced={reduced}
            onSelectHole={selectHole} onHoleEnd={handleHoleEnd}
          />
          <HolePanel
            hole={hole} tee={tee} view={view} playing={playing}
            onSelectHole={selectHole} onTee={setTee} onView={setView}
            onTour={startTour} onStop={stopTour} onReset={reset}
          />
        </div>

        <div className="mt-4 lg:mt-5 flex justify-end">
          <button
            type="button"
            onClick={() => setScoreOpen((o) => !o)}
            aria-expanded={scoreOpen}
            aria-controls="scorecard-table"
            data-testid="scorecard-toggle"
            className="flex items-center gap-2 min-h-[38px] px-4 rounded-full border border-[#c9a962]/25 text-white/70 hover:text-[#c9a962] hover:border-[#c9a962]/60 transition-colors font-montserrat text-[11px] tracking-[0.12em] uppercase focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a962]"
          >
            Carte de score
            <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-500 ${scoreOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
        </div>

        <ScoreCard open={scoreOpen} hole={hole} tee={tee} onSelectHole={selectHole} />
      </div>

      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#c9a962]/40 to-transparent" />
    </section>
  );
};

export default GolfMasterPlan;
